/**
 * Job Ledger View - Shows ledger entries and summary for a specific job
 */

import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import { AccountBalance, PlayArrow, CheckCircle, Cancel } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useJobLedger, useJobLedgerSummary } from "../hooks/useLedger.js";
import LedgerTimeline from "../components/LedgerTimeline.jsx";
import {
  PageShell,
  PageHeader,
  PageFilters,
} from "@/shared/components/pageShell";
import LedgerSummary from "../components/LedgerSummary.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { createImmediatePayoutRun, previewImmediatePayoutRun } from "@/features/payouts/services/payoutService.js";
import { getJobPayeeOptions } from "@/features/finance/services/payeeOptionsService.js";
import { getPaymentTerms } from "@/features/providers/services/paymentTermsService.js";

const JobLedgerView = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);
  
  // Payout dialog state
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [payeeOptionsLoading, setPayeeOptionsLoading] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutError, setPayoutError] = useState(null);
  const [payoutSuccess, setPayoutSuccess] = useState(null);
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  
  // Payment terms state
  const [paymentTerms, setPaymentTerms] = useState(null);
  const [paymentTermsLoading, setPaymentTermsLoading] = useState(false);
  const [paymentTermsSource, setPaymentTermsSource] = useState(null);

  // Convert date to end-of-day (23:59:59.999) so all entries on that date are included
  const asOfDateEndOfDay = useMemo(() => {
    if (!asOfDate) return new Date();
    const date = new Date(asOfDate);
    date.setHours(23, 59, 59, 999);
    return date;
  }, [asOfDate]);

  const { data: entriesData, isLoading: entriesLoading, error: entriesError, refetch: refetchEntries } = useJobLedger(jobId, {
    asOfDate: asOfDateEndOfDay,
  });

  const { data: summaryData, isLoading: summaryLoading, error: summaryError, refetch: refetchSummary } = useJobLedgerSummary(
    jobId,
    asOfDateEndOfDay
  );

  const entries = entriesData?.success ? entriesData.data : [];
  const summary = summaryData?.success ? summaryData.data : null;

  // Load job-scoped payee options when dialog opens
  const loadPayeeOptions = async () => {
    setPayeeOptionsLoading(true);
    setPreviewData(null);
    setPreviewError(null);
    try {
      const options = await getJobPayeeOptions(jobId);
      setPayeeOptions(options);
      
      // Auto-select if there's exactly one provider
      if (options.length === 1) {
        setSelectedPayee(options[0]);
      } else {
        setSelectedPayee(null);
      }
    } catch (err) {
      setPayoutError("Failed to load payee options: " + (err.message || "Unknown error"));
    } finally {
      setPayeeOptionsLoading(false);
    }
  };

  // Load payment terms when payee is selected
  useEffect(() => {
    if (!selectedPayee?.id) {
      setPaymentTerms(null);
      setPaymentTermsSource(null);
      return;
    }

    const loadPaymentTerms = async () => {
      setPaymentTermsLoading(true);
      try {
        const result = await getPaymentTerms(selectedPayee.id);
        if (result.success && result.data) {
          setPaymentTerms(result.data);
          setPaymentTermsSource(result.source);
        } else {
          setPaymentTerms(null);
          setPaymentTermsSource(null);
        }
      } catch (err) {
        setPaymentTerms(null);
        setPaymentTermsSource(null);
      } finally {
        setPaymentTermsLoading(false);
      }
    };

    loadPaymentTerms();
  }, [selectedPayee?.id]);

  // Load preview when payee is selected
  useEffect(() => {
    if (!selectedPayee?.id || !jobId) {
      setPreviewData(null);
      setPreviewError(null);
      return;
    }

    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await previewImmediatePayoutRun(jobId, selectedPayee.id);
        if (result.success) {
          // Combine data and eligibility from the response
          setPreviewData({
            ...result.data,
            eligibility: result.eligibility || null,
          });
        } else {
          setPreviewError(result.error || "Failed to load preview");
          setPreviewData(null);
        }
      } catch (err) {
        setPreviewError(err.message || "Failed to load preview");
        setPreviewData(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreview();
  }, [selectedPayee?.id, jobId]);

  // Handle immediate payout creation
  const handleImmediatePayout = async () => {
    if (!selectedPayee?.id) {
      setPayoutError("Please select a payee");
      return;
    }

    // Check eligibility before proceeding
    if (previewData?.eligibility && !previewData.eligibility.eligible) {
      setPayoutError("Cannot create payout: Provider is not eligible. See reasons below.");
      return;
    }

    setPayoutLoading(true);
    setPayoutError(null);
    setPayoutSuccess(null);

    try {
      const result = await createImmediatePayoutRun(jobId, selectedPayee.id);
      if (result.success) {
        const runId = result.data?.run?.id || result.data?.data?.run?.id;
        setPayoutSuccess(
          runId
            ? `Payout offer created (${runId}). Settle / download from the run detail or provider desk.`
            : "Payout offer created successfully."
        );
        // Refresh ledger data after a short delay
        setTimeout(() => {
          refetchEntries();
          refetchSummary();
        }, 1500);
        // Close dialog after 3 seconds
        setTimeout(() => {
          setPayoutDialogOpen(false);
          setSelectedPayee(null);
          setPreviewData(null);
          setPayoutError(null);
          setPayoutSuccess(null);
        }, 3000);
      } else {
        setPayoutError(result.error || "Failed to create payout run");
      }
    } catch (err) {
      setPayoutError(err.message || "Failed to create payout run");
    } finally {
      setPayoutLoading(false);
    }
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount || 0);
  };

  // Format fee policy helper
  const formatFeePolicy = (feePolicySnapshot) => {
    if (!feePolicySnapshot) return null;
    
    const defaults = feePolicySnapshot.defaults || {};
    const overrides = feePolicySnapshot.overrides || {};
    
    // Use overrides if available, otherwise use defaults
    const serviceRule = overrides.service || defaults.service;
    const goodsRule = overrides.goods || defaults.goods;
    
    const parts = [];
    
    if (serviceRule) {
      const serviceText = serviceRule.type === "PERCENT" 
        ? `Service: ${serviceRule.value}%`
        : `Service: ${formatCurrency(serviceRule.value)}`;
      parts.push(serviceText);
    }
    
    if (goodsRule) {
      const goodsText = goodsRule.type === "PERCENT"
        ? `Goods: ${goodsRule.value}%`
        : `Goods: ${formatCurrency(goodsRule.value)}`;
      parts.push(goodsText);
    }
    
    return parts.length > 0 ? parts.join(" • ") : null;
  };

  // Map payout trigger to template name
  const getPayoutPolicyName = (paymentTerms) => {
    if (!paymentTerms) return null;
    
    const { payoutTrigger, retentionPercent, payoutDelayDays } = paymentTerms;
    
    // Map payoutTrigger + retentionPercent to template names
    if (payoutTrigger === "PAYDAY_ONLY") {
      return "Monthly Payday Only";
    }
    if (payoutTrigger === "PAYDAY_WITH_ADVANCE") {
      return "Monthly Payday (Advance Allowed)";
    }
    if (payoutTrigger === "DEPOSIT_ONLY") {
      return "Deposit-Based";
    }
    if (payoutTrigger === "COMPLETION_PLUS_DAYS") {
      return "Next Payday Payout";
    }
    if (payoutTrigger === "FULL_PAYMENT") {
      if (retentionPercent === 0) {
        return "Immediate Payout";
      }
      if (retentionPercent === 10) {
        return "Standard Third-Party";
      }
      if (retentionPercent === 20) {
        return "Next Payday Payout";
      }
      return "Full Payment Required";
    }
    
    return null;
  };

  // Determine if payout button should be shown
  const showPayoutButton = isAdmin && summary && (summary.totalRevenue > 0 || summary.totalPayouts === 0);

  return (
    <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
      <PageHeader
        title="Job Ledger"
        back={{
          label: "Back to Order",
          shortLabel: "Order",
          onClick: () => navigate(`/orders/${jobId}`),
        }}
      />

      <PageFilters>
        <TextField
          label="As of Date"
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 200, bgcolor: "background.paper" }}
        />
      </PageFilters>

      <Box mb={3}>
        <LedgerSummary
          summary={summary}
          loading={summaryLoading}
          error={summaryError?.message}
          type="job"
        />
      </Box>

      {showPayoutButton && (
        <Box mb={3}>
          <Button
            variant="outlined"
            startIcon={<AccountBalance />}
            onClick={() => {
              setPayoutDialogOpen(true);
              loadPayeeOptions();
            }}
          >
            Create payout offer
          </Button>
        </Box>
      )}

      <Box>
        <LedgerTimeline
          entries={entries}
          loading={entriesLoading}
          error={entriesError?.message}
          title={`Financial Timeline for Job ${jobId}`}
        />
      </Box>

      {/* Immediate Payout Dialog */}
      <Dialog
        open={payoutDialogOpen}
        onClose={() => {
          if (!payoutLoading) {
          setPayoutDialogOpen(false);
          setSelectedPayee(null);
          setPreviewData(null);
          setPreviewError(null);
          setPayoutError(null);
          setPayoutSuccess(null);
          setPaymentTerms(null);
          setPaymentTermsSource(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create payout offer for {jobId}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {payoutError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPayoutError(null)}>
                {payoutError}
              </Alert>
            )}
            {payoutSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {payoutSuccess}
              </Alert>
            )}
            
            <Autocomplete
              fullWidth
              options={payeeOptions}
              value={selectedPayee}
              onChange={(_, value) => setSelectedPayee(value)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={payoutLoading || payeeOptionsLoading}
              loading={payeeOptionsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Payee"
                  placeholder={
                    payeeOptions.length === 0
                      ? "No providers found for this job"
                      : payeeOptions.length === 1
                      ? "Provider auto-selected"
                      : "Choose provider"
                  }
                  helperText={
                    payeeOptions.length > 0
                      ? `${payeeOptions.length} provider${payeeOptions.length === 1 ? "" : "s"} on this job`
                      : "No providers found with invoice items on this job"
                  }
                />
              )}
            />

            {/* Policy Information */}
            {selectedPayee?.meta && (
              <Box sx={{ mt: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Policy Information
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Chip
                      label={
                        selectedPayee.type === "PARTNER" ||
                        selectedPayee.meta.ownerType === "third_party"
                          ? "Third Party Policy"
                          : selectedPayee.meta.ownerType === "bubakii"
                          ? "Bubakii Policy"
                          : "Unknown Policy"
                      }
                      size="small"
                      color={
                        selectedPayee.type === "PARTNER" ||
                        selectedPayee.meta.ownerType === "third_party"
                          ? "default"
                          : selectedPayee.meta.ownerType === "bubakii"
                          ? "primary"
                          : "default"
                      }
                      variant="outlined"
                    />
                    {paymentTermsLoading ? (
                      <CircularProgress size={16} />
                    ) : paymentTerms && getPayoutPolicyName(paymentTerms) ? (
                      <Chip
                        label={getPayoutPolicyName(paymentTerms)}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ) : null}
                    {previewData?.feePolicySnapshot && formatFeePolicy(previewData.feePolicySnapshot) && (
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        {formatFeePolicy(previewData.feePolicySnapshot)}
                      </Typography>
                    )}
                    {(selectedPayee.type === "PARTNER"
                      ? selectedPayee.id
                      : selectedPayee.meta.ownerType === "third_party" &&
                        selectedPayee.meta.partnerId) && (
                      <Typography variant="body2" color="text.secondary">
                        Partner:{" "}
                        {selectedPayee.type === "PARTNER"
                          ? selectedPayee.id
                          : selectedPayee.meta.partnerId}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Preview Section */}
            {selectedPayee && (
              <Box sx={{ mt: 3 }}>
                {previewLoading && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Checking eligibility...
                    </Typography>
                  </Box>
                )}

                {previewError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {previewError}
                  </Alert>
                )}

                {previewData && !previewLoading && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Eligibility Status */}
                    {(previewData.eligibility || (previewData.excludedJobs && previewData.excludedJobs.length > 0)) && (
                      <Box sx={{ mb: 2 }}>
                        {previewData.eligibility?.eligible !== false &&
                        (!previewData.excludedJobs || previewData.excludedJobs.length === 0) ? (
                          <Alert
                            severity="success"
                            icon={<CheckCircle />}
                            sx={{ mb: 2 }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Provider is eligible for payout
                            </Typography>
                          </Alert>
                        ) : (
                          <Alert
                            severity="warning"
                            icon={<Cancel />}
                            sx={{ mb: 2 }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Provider is not eligible
                            </Typography>
                            {(previewData.eligibility?.reasons || 
                              (previewData.excludedJobs && previewData.excludedJobs.length > 0 && previewData.excludedJobs[0]?.reasons)) && (
                              <List dense sx={{ mt: 1, pl: 0 }}>
                                {(previewData.eligibility?.reasons || previewData.excludedJobs[0]?.reasons || []).map((reason, idx) => (
                                  <ListItem key={idx} sx={{ py: 0.5, pl: 0 }}>
                                    <ListItemText
                                      primary={reason}
                                      primaryTypographyProps={{ variant: "body2" }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Alert>
                        )}
                      </Box>
                    )}

                    {/* Preview Amounts */}
                    {previewData.totals && 
                     previewData.totals.net > 0 &&
                     previewData.eligibility?.eligible !== false &&
                     (!previewData.excludedJobs || previewData.excludedJobs.length === 0) && (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Payout Preview
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">
                              Gross Amount:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(previewData.totals.gross)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">
                              Platform Fee:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(previewData.totals.platformFee)}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" fontWeight="medium">
                              Net Payout:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {formatCurrency(previewData.totals.net)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    )}

                    {/* Excluded Jobs Info */}
                    {previewData.excludedJobs && previewData.excludedJobs.length > 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          This job would be excluded from payout:{" "}
                          {previewData.excludedJobs.map((ex) => ex.reasons?.join(", ") || "Unknown reason").join("; ")}
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
          setPayoutDialogOpen(false);
          setSelectedPayee(null);
          setPreviewData(null);
          setPreviewError(null);
          setPayoutError(null);
          setPayoutSuccess(null);
          setPaymentTerms(null);
          setPaymentTermsSource(null);
            }}
            disabled={payoutLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={payoutLoading ? <CircularProgress size={20} /> : <PlayArrow />}
            onClick={handleImmediatePayout}
            disabled={
              payoutLoading ||
              !selectedPayee?.id ||
              (previewData?.eligibility && previewData.eligibility.eligible === false) ||
              (previewData?.excludedJobs && previewData.excludedJobs.length > 0)
            }
          >
            {payoutLoading ? "Creating..." : "Create payout offer"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default JobLedgerView;
